import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUpIcon, TrendingDownIcon, RefreshCwIcon, ActivityIcon } from 'lucide-react';
interface StockData {
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePct: number;
  volume: string;
  high: number;
  low: number;
  highlight?: boolean;
}
const initialStocks: StockData[] = [{
  symbol: 'MOTHERSON',
  name: 'Motherson Sumi',
  price: 187.45,
  change: 4.2,
  changePct: 2.29,
  volume: '18.4M',
  high: 189.9,
  low: 182.1,
  highlight: true
}, {
  symbol: 'NIFTY 50',
  name: 'Nifty 50 Index',
  price: 24318.6,
  change: 142.3,
  changePct: 0.59,
  volume: '—',
  high: 24380.0,
  low: 24150.0
}, {
  symbol: 'SENSEX',
  name: 'BSE Sensex',
  price: 80218.37,
  change: 487.45,
  changePct: 0.61,
  volume: '—',
  high: 80450.0,
  low: 79820.0
}, {
  symbol: 'SAMVARDHANA',
  name: 'Samvardhana Motherson',
  price: 187.45,
  change: -1.35,
  changePct: -0.71,
  volume: '6.2M',
  high: 190.0,
  low: 185.2
}];
function MiniSparkline({
  positive
}: {
  positive: boolean;
}) {
  const points = positive ? [30, 25, 32, 28, 35, 30, 38, 34, 42, 38, 45] : [45, 42, 38, 40, 35, 38, 32, 36, 28, 32, 26];
  const max = Math.max(...points);
  const min = Math.min(...points);
  const normalize = (v: number) => 28 - (v - min) / (max - min) * 24;
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${i * 10} ${normalize(p)}`).join(' ');
  return <svg width="100" height="32" viewBox="0 0 100 32" className="overflow-visible" data-id="element-556">
      <path d={path} fill="none" stroke={positive ? '#34D399' : '#F87171'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.8" data-id="element-557" />
    </svg>;
}
export function StockWidget() {
  const [stocks, setStocks] = useState(initialStocks);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Simulate live price fluctuations
  useEffect(() => {
    const interval = setInterval(() => {
      setStocks(prev => prev.map(stock => {
        const delta = (Math.random() - 0.48) * (stock.highlight ? 0.8 : 2);
        const newPrice = Math.max(stock.price + delta, 1);
        const newChange = stock.change + delta;
        const newChangePct = newChange / (newPrice - newChange) * 100;
        return {
          ...stock,
          price: newPrice,
          change: newChange,
          changePct: newChangePct
        };
      }));
      setLastUpdated(new Date());
    }, 4000);
    return () => clearInterval(interval);
  }, []);
  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => setIsRefreshing(false), 1000);
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="rounded-2xl overflow-hidden h-full flex flex-col" style={{
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(201,168,76,0.2)'
  }} data-id="element-558">
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-[rgba(201,168,76,0.12)]" data-id="element-559">
        <div className="flex items-center gap-3" data-id="element-560">
          <div className="w-9 h-9 rounded-xl bg-[rgba(201,168,76,0.15)] flex items-center justify-center" data-id="element-561">
            <ActivityIcon className="w-4 h-4 text-[#C9A84C]" data-id="element-562" />
          </div>
          <div data-id="element-563">
            <h3 className="text-base font-semibold text-white" style={{
            fontFamily: "'Playfair Display', serif"
          }} data-id="element-564">
              Markets
            </h3>
            <p className="text-xs text-gray-400" data-id="element-565">NSE · BSE · Live</p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-566">
          <div className="flex items-center gap-1.5" data-id="element-567">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" data-id="element-568" />
            <span className="text-xs text-gray-400" data-id="element-569">
              {lastUpdated.toLocaleTimeString('en-IN', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            })}
            </span>
          </div>
          <button onClick={handleRefresh} className="w-8 h-8 rounded-lg bg-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.1)] flex items-center justify-center transition-colors" data-id="element-570">
            <RefreshCwIcon className={`w-3.5 h-3.5 text-gray-400 ${isRefreshing ? 'animate-spin' : ''}`} data-id="element-571" />
          </button>
        </div>
      </div>

      {/* Stocks */}
      <div className="flex-1 divide-y divide-[rgba(255,255,255,0.04)]" data-id="element-572">
        {stocks.map((stock, index) => {
        const isPositive = stock.change >= 0;
        return <motion.div key={stock.symbol} initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.06 * index
        }} className={`flex items-center gap-4 px-5 py-3.5 ${stock.highlight ? 'bg-[rgba(201,168,76,0.06)]' : ''}`} data-id="element-573">
              {/* Symbol */}
              <div className="w-28 flex-shrink-0" data-id="element-574">
                <div className="flex items-center gap-2" data-id="element-575">
                  {stock.highlight && <div className="w-1.5 h-1.5 rounded-full bg-[#C9A84C]" data-id="element-576" />}
                  <span className={`text-sm font-bold ${stock.highlight ? 'text-[#C9A84C]' : 'text-white'}`} data-id="element-577">
                    {stock.symbol}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5 truncate" data-id="element-578">
                  {stock.name}
                </p>
              </div>

              {/* Sparkline */}
              <div className="flex-shrink-0 opacity-70" data-id="element-579">
                <MiniSparkline positive={isPositive} data-id="element-580" />
              </div>

              {/* Price */}
              <div className="flex-1 text-right" data-id="element-581">
                <AnimatePresence mode="wait" data-id="element-582">
                  <motion.p key={stock.price.toFixed(2)} initial={{
                opacity: 0.5,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} className="text-base font-semibold text-white tabular-nums" data-id="element-583">
                    ₹
                    {stock.price.toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                  </motion.p>
                </AnimatePresence>
                <div className={`flex items-center justify-end gap-1 mt-0.5 ${isPositive ? 'text-green-400' : 'text-red-400'}`} data-id="element-584">
                  {isPositive ? <TrendingUpIcon className="w-3 h-3" data-id="element-585" /> : <TrendingDownIcon className="w-3 h-3" data-id="element-586" />}
                  <span className="text-xs font-medium tabular-nums" data-id="element-587">
                    {isPositive ? '+' : ''}
                    {stock.change.toFixed(2)} ({isPositive ? '+' : ''}
                    {stock.changePct.toFixed(2)}%)
                  </span>
                </div>
              </div>

              {/* H/L */}
              <div className="w-24 flex-shrink-0 text-right hidden xl:block" data-id="element-588">
                <p className="text-xs text-gray-500" data-id="element-589">
                  H:{' '}
                  <span className="text-gray-300" data-id="element-590">
                    ₹{stock.high.toFixed(0)}
                  </span>
                </p>
                <p className="text-xs text-gray-500" data-id="element-591">
                  L:{' '}
                  <span className="text-gray-300" data-id="element-592">₹{stock.low.toFixed(0)}</span>
                </p>
              </div>
            </motion.div>;
      })}
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-[rgba(201,168,76,0.1)] flex items-center justify-between" data-id="element-593">
        <span className="text-xs text-gray-600" data-id="element-594">
          Data delayed 15 min · NSE/BSE
        </span>
        <span className="text-xs text-[#C9A84C] opacity-70" data-id="element-595">
          Motherson Group
        </span>
      </div>
    </motion.div>;
}